import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import * as echarts from 'echarts';

import { ApiService } from '../../core/api.service';

interface Restaurant {
  id: number;
  name: string;
  slug?: string;
  isOpen?: boolean;
}

interface DashboardSummary {
  revenue: number;
  previousRevenue: number;
  orders: number;
  finishedOrders: number;
  averageTicket: number;
  canceledOrders: number;
  openOrders: number;
  growthPercentage: number;
}

interface SalesByDay {
  date: string;
  revenue: number;
  orders: number;
}

interface OrdersByStatus {
  status: string;
  label: string;
  total: number;
}

interface OrdersByType {
  type: string;
  label: string;
  total: number;
}

interface OrdersByHour {
  hour: string;
  total: number;
}

interface TopProduct {
  productId: number;
  name: string;
  quantity: number;
  revenue: number;
}

interface DashboardResponse {
  restaurant: Restaurant;

  period: {
    startDate: string;
    endDate: string;
    previousStartDate: string;
    previousEndDate: string;
  };

  summary: DashboardSummary;

  salesByDay: SalesByDay[];
  ordersByStatus: OrdersByStatus[];
  ordersByType: OrdersByType[];
  ordersByHour: OrdersByHour[];
  topProducts: TopProduct[];
  paymentMethods: any[];
}

@Component({
  selector: 'app-dashboard-data',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './dashboard-data.component.html',
  styleUrl: './dashboard-data.component.css'
})
export class DashboardDataComponent
  implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('salesChart')
  salesChartElement?: ElementRef<HTMLDivElement>;

  @ViewChild('statusChart')
  statusChartElement?: ElementRef<HTMLDivElement>;

  @ViewChild('productsChart')
  productsChartElement?: ElementRef<HTMLDivElement>;

  @ViewChild('hoursChart')
  hoursChartElement?: ElementRef<HTMLDivElement>;

  @ViewChild('typesChart')
  typesChartElement?: ElementRef<HTMLDivElement>;

  restaurants: Restaurant[] = [];

  restaurantId: number | null = null;

  selectedPeriod = '7days';

  startDate = '';
  endDate = '';

  loadingRestaurants = false;
  loadingDashboard = false;

  error = '';

  viewInitialized = false;

  dashboard: DashboardResponse =
    this.createEmptyDashboard();

  private salesChart?: echarts.ECharts;
  private statusChart?: echarts.ECharts;
  private productsChart?: echarts.ECharts;
  private hoursChart?: echarts.ECharts;
  private typesChart?: echarts.ECharts;

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    this.setPeriod('7days');
    this.loadRestaurants();
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;

    if (!this.loadingDashboard) {
      setTimeout(() => {
        this.renderCharts();
      });
    }
  }

  ngOnDestroy(): void {
    this.disposeCharts();
  }

  private createEmptyDashboard(): DashboardResponse {
    return {
      restaurant: {
        id: 0,
        name: ''
      },

      period: {
        startDate: '',
        endDate: '',
        previousStartDate: '',
        previousEndDate: ''
      },

      summary: {
        revenue: 0,
        previousRevenue: 0,
        orders: 0,
        finishedOrders: 0,
        averageTicket: 0,
        canceledOrders: 0,
        openOrders: 0,
        growthPercentage: 0
      },

      salesByDay: [],
      ordersByStatus: [],
      ordersByType: [],
      ordersByHour: [],
      topProducts: [],
      paymentMethods: []
    };
  }

  loadRestaurants(): void {
    this.loadingRestaurants = true;
    this.error = '';

    this.api.get<Restaurant[]>('/restaurants')
      .subscribe({
        next: restaurants => {
          this.restaurants = restaurants || [];
          this.loadingRestaurants = false;

          if (this.restaurants.length) {
            this.restaurantId = this.restaurants[0].id;
            this.loadDashboard();
          }
        },

        error: error => {
          this.loadingRestaurants = false;

          this.error =
            error?.error?.message ||
            'Erro ao carregar os restaurantes.';
        }
      });
  }

  onRestaurantChange(): void {
    this.loadDashboard();
  }

  setPeriod(period: string): void {
    this.selectedPeriod = period;

    const today = this.createLocalDate();

    let start = this.createLocalDate();
    let end = this.createLocalDate();

    switch (period) {
      case 'today':
        break;

      case 'yesterday':
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
        break;

      case '7days':
        start.setDate(start.getDate() - 6);
        break;

      case '30days':
        start.setDate(start.getDate() - 29);
        break;

      case 'month':
        start = new Date(
          today.getFullYear(),
          today.getMonth(),
          1
        );
        break;

      case 'custom':
        if (!this.startDate) {
          this.startDate = this.formatDateInput(today);
        }

        if (!this.endDate) {
          this.endDate = this.formatDateInput(today);
        }

        return;
    }

    this.startDate = this.formatDateInput(start);
    this.endDate = this.formatDateInput(end);

    if (this.restaurantId) {
      this.loadDashboard();
    }
  }

  applyCustomPeriod(): void {
    this.error = '';

    if (!this.startDate || !this.endDate) {
      this.error =
        'Informe a data inicial e a data final.';
      return;
    }

    if (this.startDate > this.endDate) {
      this.error =
        'A data inicial não pode ser maior que a data final.';
      return;
    }

    this.selectedPeriod = 'custom';
    this.loadDashboard();
  }

  loadDashboard(): void {
    if (
      !this.restaurantId ||
      !this.startDate ||
      !this.endDate
    ) {
      return;
    }

    this.loadingDashboard = true;
    this.error = '';

    const params = new URLSearchParams({
      restaurantId: String(this.restaurantId),
      startDate: this.startDate,
      endDate: this.endDate
    });

    this.api
      .get<DashboardResponse>(
        `/dashboard?${params.toString()}`
      )
      .subscribe({
        next: data => {
          this.dashboard = data;
          this.loadingDashboard = false;

          setTimeout(() => {
            this.renderCharts();
          });
        },

        error: error => {
          this.loadingDashboard = false;

          this.error =
            error?.error?.message ||
            'Erro ao carregar o dashboard.';

          this.dashboard =
            this.createEmptyDashboard();

          this.disposeCharts();
        }
      });
  }

  private renderCharts(): void {
    if (!this.viewInitialized) {
      return;
    }

    this.renderSalesChart();
    this.renderStatusChart();
    this.renderProductsChart();
    this.renderHoursChart();
    this.renderTypesChart();
  }

  private renderSalesChart(): void {
    const element =
      this.salesChartElement?.nativeElement;

    if (!element) {
      return;
    }

    this.salesChart?.dispose();
    this.salesChart = echarts.init(element);

    const data = this.dashboard.salesByDay || [];

    this.salesChart.setOption({
      tooltip: {
        trigger: 'axis'
      },

      legend: {
        data: [
          'Faturamento',
          'Pedidos'
        ]
      },

      grid: {
        left: 20,
        right: 20,
        top: 50,
        bottom: 20,
        containLabel: true
      },

      xAxis: {
        type: 'category',
        data: data.map(item =>
          this.formatShortDate(item.date)
        )
      },

      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: number) =>
              this.formatCompactMoney(value)
          }
        },
        {
          type: 'value',
          minInterval: 1
        }
      ],

      series: [
        {
          name: 'Faturamento',
          type: 'line',
          smooth: true,
          data: data.map(item =>
            Number(item.revenue || 0)
          )
        },
        {
          name: 'Pedidos',
          type: 'bar',
          yAxisIndex: 1,
          data: data.map(item =>
            Number(item.orders || 0)
          )
        }
      ]
    });
  }

  private renderStatusChart(): void {
    const element =
      this.statusChartElement?.nativeElement;

    if (!element) {
      return;
    }

    this.statusChart?.dispose();
    this.statusChart = echarts.init(element);

    const data = (
      this.dashboard.ordersByStatus || []
    )
      .filter(item => Number(item.total) > 0)
      .map(item => ({
        name: item.label,
        value: Number(item.total)
      }));

    this.statusChart.setOption({
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} pedido(s) ({d}%)'
      },

      legend: {
        bottom: 0
      },

      series: [
        {
          type: 'pie',
          radius: [
            '45%',
            '70%'
          ],
          data
        }
      ]
    });
  }

  private renderProductsChart(): void {
    const element =
      this.productsChartElement?.nativeElement;

    if (!element) {
      return;
    }

    this.productsChart?.dispose();
    this.productsChart = echarts.init(element);

    const products = [
      ...(this.dashboard.topProducts || [])
    ]
      .slice(0, 10)
      .reverse();

    this.productsChart.setOption({
      tooltip: {
        trigger: 'axis'
      },

      grid: {
        left: 20,
        right: 30,
        top: 20,
        bottom: 20,
        containLabel: true
      },

      xAxis: {
        type: 'value',
        minInterval: 1
      },

      yAxis: {
        type: 'category',
        data: products.map(
          product => product.name
        )
      },

      series: [
        {
          type: 'bar',
          data: products.map(
            product => product.quantity
          )
        }
      ]
    });
  }

  private renderHoursChart(): void {
    const element =
      this.hoursChartElement?.nativeElement;

    if (!element) {
      return;
    }

    this.hoursChart?.dispose();
    this.hoursChart = echarts.init(element);

    const data =
      this.dashboard.ordersByHour || [];

    this.hoursChart.setOption({
      tooltip: {
        trigger: 'axis'
      },

      grid: {
        left: 20,
        right: 20,
        top: 20,
        bottom: 40,
        containLabel: true
      },

      xAxis: {
        type: 'category',
        data: data.map(item => item.hour)
      },

      yAxis: {
        type: 'value',
        minInterval: 1
      },

      series: [
        {
          type: 'bar',
          data: data.map(
            item => Number(item.total || 0)
          )
        }
      ]
    });
  }

  private renderTypesChart(): void {
    const element =
      this.typesChartElement?.nativeElement;

    if (!element) {
      return;
    }

    this.typesChart?.dispose();
    this.typesChart = echarts.init(element);

    const data = (
      this.dashboard.ordersByType || []
    )
      .filter(item => Number(item.total) > 0)
      .map(item => ({
        name: item.label,
        value: Number(item.total)
      }));

    this.typesChart.setOption({
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} pedido(s) ({d}%)'
      },

      legend: {
        bottom: 0
      },

      series: [
        {
          type: 'pie',
          radius: [
            '40%',
            '70%'
          ],
          data
        }
      ]
    });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.salesChart?.resize();
    this.statusChart?.resize();
    this.productsChart?.resize();
    this.hoursChart?.resize();
    this.typesChart?.resize();
  }

  private disposeCharts(): void {
    this.salesChart?.dispose();
    this.statusChart?.dispose();
    this.productsChart?.dispose();
    this.hoursChart?.dispose();
    this.typesChart?.dispose();

    this.salesChart = undefined;
    this.statusChart = undefined;
    this.productsChart = undefined;
    this.hoursChart = undefined;
    this.typesChart = undefined;
  }

  createLocalDate(): Date {
    const now = new Date();

    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
  }

  formatDateInput(date: Date): string {
    const year = date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, '0');

    const day = String(
      date.getDate()
    ).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  formatShortDate(dateValue: string): string {
    if (!dateValue) {
      return '';
    }

    const parts = dateValue.split('-');

    if (parts.length !== 3) {
      return dateValue;
    }

    return `${parts[2]}/${parts[1]}`;
  }

  formatFullDate(dateValue: string): string {
    if (!dateValue) {
      return '';
    }

    const parts = dateValue.split('-');

    if (parts.length !== 3) {
      return dateValue;
    }

    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }

  formatMoney(value: any): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(Number(value || 0));
  }

  formatCompactMoney(value: number): string {
    const number = Number(value || 0);

    if (number >= 1_000_000) {
      return `R$ ${(number / 1_000_000)
        .toFixed(1)
        .replace('.', ',')} mi`;
    }

    if (number >= 1_000) {
      return `R$ ${(number / 1_000)
        .toFixed(1)
        .replace('.', ',')} mil`;
    }

    return `R$ ${number.toFixed(0)}`;
  }

  formatPercentage(value: any): string {
    const number = Number(value || 0);

    return `${number.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    })}%`;
  }

  getGrowthClass(): string {
    const growth = Number(
      this.dashboard.summary.growthPercentage || 0
    );

    if (growth > 0) {
      return 'positive';
    }

    if (growth < 0) {
      return 'negative';
    }

    return 'neutral';
  }

  getGrowthIcon(): string {
    const growth = Number(
      this.dashboard.summary.growthPercentage || 0
    );

    if (growth > 0) {
      return '↑';
    }

    if (growth < 0) {
      return '↓';
    }

    return '→';
  }

  hasOrders(): boolean {
    return Number(
      this.dashboard.summary.orders || 0
    ) > 0;
  }
}